import { useEffect, useState } from "react";
import { Col, FormGroup, Input, InputGroup, InputGroupText, Label, Row } from "reactstrap";
import { useTranslation } from "react-i18next";
import AppSelect from "../../../../components/Forms/AppSelect";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import PageSubHeader from "../../../../components/Layouts/PageSubHeader";
import SidebarLayout from "../../../../layouts/SidebarLayout";
import sidebar from "../../utils/sidebar";
import DatePicker from "react-datepicker";
import { useSettings } from "../../../../context/settings";
import FixedSave from "../../../../components/Actions/FixedSave";
import CustomSelect from "../../../../components/Forms/CustomSelect";
import { toast } from "react-toastify";
import { format } from "date-fns";
import { get, postForm } from "../../../../utils/helpers/api";

function CreateExpensePage() {
  const { t } = useTranslation();
  const { state } = useLocation();
  const { id } = useParams();
  const { datePickerFormat } = useSettings();
  const navigate = useNavigate();
  const [initLoading, setInitLoading] = useState(true);
  const [companySettings, setCompanySettings] = useState({});
  const [saveLoading, setSaveLoading] = useState(false);
  const [expenseData, setExpenseData] = useState({
    title: state?.expenseData?.title || '',
    date: state?.expenseData?.date || '',
    category: state?.expenseData?.category || '',
    amount: state?.expenseData?.amount || '',
    payment_method: state?.expenseData?.payment_method || '',
    vendor: state?.expenseData?.vendor || '',
    notes: state?.expenseData?.notes || '',
    attachment: null
  });

  useEffect(() => {
    initData();
  }, []);

  const initData = async () => {
    setInitLoading(true);
    const data = await get('/accounting/expenses/create');

    if(data.success) {
      setCompanySettings(data.company_settings);

      setInitLoading(false);
    }
  }

  const saveExpense = async () => {
    setSaveLoading(true);
    const formData = new FormData();
    formData.append('title', expenseData.title);
    formData.append('date', format(expenseData.date, 'yyyy-MM-dd'));
    formData.append('category', expenseData.category);
    formData.append('amount', expenseData.amount);
    formData.append('payment_method', expenseData.payment_method);
    formData.append('vendor', expenseData.vendor);
    formData.append('notes', expenseData.notes);
    if (expenseData.attachment) {
      formData.append('attachment', expenseData.attachment);
    }

    const url = id ? `/accounting/expenses/update/${id}` : '/accounting/expenses/store';
    Promise.resolve(postForm(url, formData))
    .then(data => {
      if(data?.success) {
        navigate('/accounting/expenses');
      } else {
        toast.error(data?.message || t('accounting.saveExpenseError'));
      }
      setSaveLoading(false);
    })
    .catch(err => {
      console.error(err);
      setSaveLoading(false);
    });
  };

  return(
    <SidebarLayout sidebar={sidebar}>
      <div className="page-with-subheader">
        <PageSubHeader
          breadcrumb={[t('accounting.expenses'), id ? t('accounting.editExpense') : t('accounting.newExpense')]}
          onBack={() => navigate('/accounting/expenses')}
        />

        <div className="subheader-page-content scrollable">
          <Row>
            <Col md="6" className="mb-3">
              <Label>{t('common.title')}: </Label>
              <Input
                type="text"
                value={expenseData.title}
                onChange={(e) => setExpenseData({ ...expenseData, title: e.target.value })}
                className="form-control"
              />
            </Col>
          </Row>

          <Row>
            <Col md="6" className="mb-3">
              <Label>{t('common.date')}: </Label>
              <DatePicker
                selected={expenseData.date}
                onChange={(d) => setExpenseData({ ...expenseData, date: d })}
                className="form-control"
                dateFormat={datePickerFormat}
              />
            </Col>
          </Row>

          <Row>
            <Col md="6" className="mb-3">
              <Label>{t('common.amount')}: </Label>
              <InputGroup>
                <InputGroupText>{ companySettings?.sales?.base_currency }</InputGroupText>
                <Input
                  type="number"
                  value={expenseData.amount}
                  onChange={(e) => setExpenseData({ ...expenseData, amount: e.target.value })}
                />
              </InputGroup>
            </Col>
          </Row>

          <Row>
            <Col md="6" className="mb-3">
              <Label>{t('accounting.category')}: </Label>
              <CustomSelect
                module="accounting/expense-categories"
                responseKey="expense_categories"
                value={expenseData.category}
                onChange={selected => {
                  setExpenseData({ ...expenseData, category: selected ? selected.label : null });
                }}
                placeholder={t('accounting.searchCategoryPlaceholder')}
                canCreate={false}
                createLink="/accounting/expense-categories/create"
              />
            </Col>
          </Row>

          <Row>
            <Col md="6" className="mb-3">
              <Label>{t('sales.paymentMode')}: </Label>
              <AppSelect
                options={[
                  { value: 'cash', label: t('sales.cash') },
                  { value: 'credit_card', label: t('accounting.creditCard') },
                  { value: 'bank_transfer', label: t('sales.bankTransfer') },
                ]}
                value={expenseData.payment_method}
                placeholder={t('common.select')}
                isClearable
                onChange={value => setExpenseData({ ...expenseData, payment_method: value })}
              />
            </Col>
          </Row>

          <Row>
            <Col md="6" className="mb-3">
              <Label>{t('accounting.vendor')}: </Label>
              <CustomSelect
                module="accounting/expense-vendors"
                responseKey="expense_vendors"
                value={expenseData.vendor}
                onChange={selected => {
                  setExpenseData({ ...expenseData, vendor: selected ? selected.label : null });
                }}
                placeholder={t('accounting.searchVendorPlaceholder')}
                canCreate={false}
                createLink="/accounting/expense-vendors/create"
              />
            </Col>
          </Row>

          <Row>
            <Col md="6" className="mb-3">
              <Label>{t('attachments.attachment')}</Label>
              <Input
                type="file"
                onChange={e => setExpenseData({ ...expenseData, attachment: e.target.files[0] })}
              />
            </Col>
          </Row>

          <Row>
            <Col md="6" className="mb-3">
              <Label>{t('common.notes')}: </Label>
              <Input
                type="textarea"
                value={expenseData.notes}
                onChange={(e) => setExpenseData({ ...expenseData, notes: e.target.value })}
                className="form-control"
              />
            </Col>
          </Row>
        </div>
      </div>

      <FixedSave
        onSave={saveExpense}
        loading={saveLoading}
        disabled={saveLoading || !expenseData?.date}
      />
    </SidebarLayout>
  );
}

export default CreateExpensePage;